import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Camera,
  Keyboard,
  Loader2,
  LogOut,
  Package,
  QrCode,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useIsMobile } from './ui/use-mobile';
import { useAuth } from '../contexts/AuthContext';
import { rawItemApiService, RmQrScanResult } from '../services/rawItemApi';
import { formatQty3 } from '../utils/quantityUtils';

type ScanPhase = 'idle' | 'scanning' | 'loading' | 'success' | 'error';

const CAMERA_ELEMENT_ID = 'rm-qr-reader';
const DECODE_COOLDOWN_MS = 1500;

function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right break-all">{value}</span>
    </div>
  );
}

function RmScanResultCard({
  result,
  showOpenErp,
}: {
  result: RmQrScanResult;
  showOpenErp?: boolean;
}) {
  const item = result.rawItem;
  const navigate = useNavigate();
  const hasHeat = !!(result.heatNo && String(result.heatNo).trim());

  return (
    <Card className="border-primary-200 shadow-md">
      <CardHeader className="pb-2 bg-gradient-to-r from-primary-50 to-white">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
            <Package className="h-5 w-5 text-primary-700" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-lg text-primary-900 leading-snug">
              {item?.itemName || 'Raw Item'}
            </CardTitle>
            <p className="text-sm text-gray-600 mt-0.5">SKU: {result.sku || item?.sku || '—'}</p>
            {item?.isActive === false && (
              <Badge variant="outline" className="mt-1 border-amber-300 text-amber-800 bg-amber-50">
                Inactive
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-3 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Master</p>
          <DetailRow label="Category" value={item?.itemCategory?.categoryName || item?.categoryName} />
          <DetailRow label="Unit" value={item?.itemUnit} />
          <DetailRow label="Grade" value={item?.grade} />
          <DetailRow label="Size" value={item?.size} />
          <DetailRow label="Color" value={item?.color} />
          <DetailRow label="Shape" value={item?.shape} />
          <DetailRow label="HSN" value={item?.hsn?.hsnCode} />
          <DetailRow
            label="Min buffer"
            value={item?.minBufferStock != null ? formatQty3(item.minBufferStock) : undefined}
          />
        </div>

        {hasHeat && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Heat / GRN</p>
            <DetailRow label="Heat no" value={result.heatNo} />
            <DetailRow label="Supplier heat" value={result.supplierHeatNo} />
            <DetailRow label="Supplier" value={result.supplierName} />
            <DetailRow
              label="Available qty"
              value={
                result.availableQuantity != null ? formatQty3(result.availableQuantity) : undefined
              }
            />
            <DetailRow
              label="Total received"
              value={
                result.totalReceivedQuantity != null
                  ? formatQty3(result.totalReceivedQuantity)
                  : undefined
              }
            />
            <DetailRow label="Quality" value={result.qualityCheck} />
            <DetailRow
              label="GRN(s)"
              value={
                result.grnNumbers && result.grnNumbers.length > 0
                  ? result.grnNumbers.join(', ')
                  : undefined
              }
            />
          </div>
        )}

        {showOpenErp && item?.id != null && (
          <Button
            variant="outline"
            className="w-full border-primary-200 text-primary-700"
            onClick={() => navigate(`/raw-items/${item.id}?view=true`)}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Raw Item
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function RmQrScannerPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [phase, setPhase] = useState<ScanPhase>('idle');
  const [manualCode, setManualCode] = useState('');
  const [result, setResult] = useState<RmQrScanResult | null>(null);
  const [useCamera, setUseCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastDecodeAtRef = useRef(0);
  const scanningLockRef = useRef(false);
  const isMobileRef = useRef(isMobile);
  const resolveCodeRef = useRef<(raw: string) => Promise<void>>(async () => {});
  const startCameraRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) return;
    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
      scanner.clear();
    } catch {
      // ignore stop errors
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setResult(null);
    setPhase('scanning');
    setUseCamera(true);
    await stopCamera();

    await new Promise((r) => setTimeout(r, 80));
    try {
      const scanner = new Html5Qrcode(CAMERA_ELEMENT_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 8, qrbox: { width: 250, height: 250 } },
        (decoded) => {
          void resolveCodeRef.current(decoded);
        },
        () => {
          /* ignore frame miss */
        },
      );
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Unable to start camera. Check HTTPS and permissions.';
      setCameraError(msg);
      toast.error(msg);
      setPhase('idle');
      setUseCamera(false);
    }
  }, [stopCamera]);

  const resolveCode = useCallback(
    async (raw: string) => {
      const code = raw.trim();
      if (!code || scanningLockRef.current) return;
      const now = Date.now();
      if (now - lastDecodeAtRef.current < DECODE_COOLDOWN_MS) return;
      lastDecodeAtRef.current = now;
      scanningLockRef.current = true;
      setPhase('loading');
      try {
        await stopCamera();
        setUseCamera(false);
        const data = await rawItemApiService.scanRmQr(code);
        setResult(data);
        setPhase('success');
        setManualCode('');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Scan failed';
        toast.error(msg || 'Item not found');
        setResult(null);
        if (isMobileRef.current) {
          scanningLockRef.current = false;
          void startCameraRef.current();
          return;
        }
        setPhase('idle');
        requestAnimationFrame(() => inputRef.current?.focus());
      } finally {
        scanningLockRef.current = false;
      }
    },
    [stopCamera],
  );

  useEffect(() => {
    resolveCodeRef.current = resolveCode;
  }, [resolveCode]);

  useEffect(() => {
    startCameraRef.current = startCamera;
  }, [startCamera]);

  useEffect(() => {
    if (isMobile) {
      void startCamera();
    }
    return () => {
      void stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount only when mobile flag settles
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile && phase === 'idle' && !useCamera) {
      inputRef.current?.focus();
    }
  }, [isMobile, phase, useCamera, result]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void resolveCode(manualCode);
  };

  const handleScanAgain = () => {
    setResult(null);
    setPhase('idle');
    if (isMobile) {
      void startCamera();
    } else {
      setUseCamera(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleLogout = () => {
    void stopCamera();
    logout();
    navigate('/login', { replace: true });
  };

  const handleBack = () => {
    void stopCamera();
    if (isMobile) navigate('/m');
    else navigate('/raw-items');
  };

  // —— Mobile ——
  if (isMobile) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <header className="flex items-center justify-between px-3 py-2 bg-black/80 z-10">
          <Button variant="ghost" size="sm" className="text-white" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <span className="text-sm font-medium">Scan RM</span>
          <Button variant="ghost" size="sm" className="text-white" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </header>

        {phase === 'success' && result ? (
          <div className="flex-1 overflow-y-auto bg-gray-50 text-gray-900 p-4 space-y-4">
            <RmScanResultCard result={result} />
            <Button
              className="w-full h-12 bg-primary-600 hover:bg-primary-700 text-white"
              onClick={handleScanAgain}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Scan again
            </Button>
          </div>
        ) : (
          <div className="flex-1 relative flex flex-col">
            <div id={CAMERA_ELEMENT_ID} className="flex-1 w-full min-h-[60vh] bg-black" />
            {phase === 'loading' && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-white" />
              </div>
            )}
            <div className="p-4 bg-gray-900 space-y-3">
              <p className="text-center text-sm text-gray-300">
                Point at RM QR label (SKU or SKU+HEAT)
              </p>
              {cameraError && <p className="text-center text-sm text-red-400">{cameraError}</p>}
              <form onSubmit={handleManualSubmit} className="flex gap-2">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Or paste / type code"
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <Button type="submit" disabled={phase === 'loading'}>
                  Go
                </Button>
              </form>
              {cameraError && (
                <Button variant="outline" className="w-full" onClick={() => void startCamera()}>
                  <Camera className="h-4 w-4 mr-2" />
                  Retry camera
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // —— Desktop ——
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-gray-50">
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={handleBack} className="border-primary-200">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-primary-900 flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            RM Scanner
          </h1>
          <div className="w-24" />
        </div>

        <Card className="border-primary-200">
          <CardHeader>
            <CardTitle className="text-base">Scan or enter code</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!useCamera && (
              <form onSubmit={handleManualSubmit} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="rm-scan-input">USB scanner / manual paste</Label>
                  <Input
                    id="rm-scan-input"
                    ref={inputRef}
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Scan barcode or paste IB24|SG-GROUP|RM|v1|SKU=..."
                    className="border-primary-200 font-mono"
                    autoFocus
                    disabled={phase === 'loading'}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={phase === 'loading' || !manualCode.trim()}>
                    {phase === 'loading' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Keyboard className="h-4 w-4 mr-2" />
                    )}
                    Lookup
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void startCamera()}
                    disabled={phase === 'loading'}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Use camera
                  </Button>
                </div>
              </form>
            )}

            {useCamera && (
              <div className="space-y-3">
                <div id={CAMERA_ELEMENT_ID} className="w-full min-h-[280px] rounded-lg overflow-hidden bg-black" />
                {phase === 'loading' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Looking up…
                  </div>
                )}
                {cameraError && <p className="text-sm text-red-600">{cameraError}</p>}
                <Button
                  variant="outline"
                  onClick={async () => {
                    await stopCamera();
                    setUseCamera(false);
                    setPhase('idle');
                  }}
                >
                  Switch to keyboard / USB
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {phase === 'success' && result && (
          <div className="space-y-3">
            <RmScanResultCard result={result} showOpenErp />
            <Button className="w-full" onClick={handleScanAgain}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Scan again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
